import { DEFAULT_STATE, LICENSE_TYPE } from '../constants'
import { searchPageStore } from '../index'

/**
 * Gets the current licenses value, always returns an array
 * @returns {string[]} - Array of license types, defaults to ["all"] if empty/undefined
 */
const getCurrentLicenses = () => {
  const state = searchPageStore.getState()
  const licenses = state.licenses

  if (!Array.isArray(licenses) || licenses.length === 0) {
    return DEFAULT_STATE.licenses
  }

  return licenses
}

/**
 * Sets the licenses filter
 * @param {string[]} licenses - Either an array with ["all"] or an array of license types
 */
export const setLicenses = (licenses) => {
  if (licenses.includes(LICENSE_TYPE.ALL)) {
    searchPageStore.setState({ licenses: [LICENSE_TYPE.ALL] })
    return
  }

  searchPageStore.setState({ licenses })
}

/**
 * Adds a license type to the array
 * @param {string} license - Either "all" or one of the license types
 */
export const addLicense = (license) => {
  if (license === LICENSE_TYPE.ALL) {
    searchPageStore.setState({ licenses: [LICENSE_TYPE.ALL] })
    return
  }

  const currentLicenses = getCurrentLicenses()

  if (currentLicenses.length === 1 && currentLicenses[0] === LICENSE_TYPE.ALL) {
    searchPageStore.setState({ licenses: [license] })
    return
  }

  if (!currentLicenses.includes(license)) {
    searchPageStore.setState({ licenses: [...currentLicenses, license] })
  }
}

/**
 * Removes a license type from the array
 * @param {string} license - One of the license types (not "all")
 */
export const removeLicense = (license) => {
  const currentLicenses = getCurrentLicenses()

  if (currentLicenses.length === 1 && currentLicenses[0] === LICENSE_TYPE.ALL) {
    return
  }

  const newLicenses = currentLicenses.filter((l) => l !== license)

  searchPageStore.setState({ licenses: newLicenses.length === 0 ? [LICENSE_TYPE.ALL] : newLicenses })
}

/**
 * Sets the AI Usage allowed flag
 * @param {string} aiUsageAllowed - Either AI_USAGE_TYPE.AI_USAGE_ALLOWED or AI_USAGE_TYPE.NO_AI_USAGE_ALLOWED
 */
export const setAiUsageAllowed = (aiUsageAllowed) => {
  const currentLicenses = getCurrentLicenses()

  if (currentLicenses.includes(LICENSE_TYPE.ALL)) {
    searchPageStore.setState({ aiUsageAllowed })

    return
  }

  const nextLicenses = []

  if (aiUsageAllowed) {
    if (currentLicenses.includes(LICENSE_TYPE.ROYALTY_FREE_NO_AI)) {
      nextLicenses.push(LICENSE_TYPE.ROYALTY_FREE)
    }
    if (currentLicenses.includes(LICENSE_TYPE.EDITORIAL_NO_AI)) {
      nextLicenses.push(LICENSE_TYPE.EDITORIAL)
    }
    if (currentLicenses.includes(LICENSE_TYPE.CUSTOM_NO_AI)) {
      nextLicenses.push(LICENSE_TYPE.CUSTOM)
    }

    searchPageStore.setState({ licenses: nextLicenses, aiUsageAllowed })

    return
  }

  if (currentLicenses.includes(LICENSE_TYPE.ROYALTY_FREE)) {
    nextLicenses.push(LICENSE_TYPE.ROYALTY_FREE_NO_AI)
  }
  if (currentLicenses.includes(LICENSE_TYPE.EDITORIAL)) {
    nextLicenses.push(LICENSE_TYPE.EDITORIAL_NO_AI)
  }
  if (currentLicenses.includes(LICENSE_TYPE.CUSTOM)) {
    nextLicenses.push(LICENSE_TYPE.CUSTOM_NO_AI)
  }

  searchPageStore.setState({ licenses: nextLicenses, aiUsageAllowed })
}

/**
 * Clears all licenses and AI Usage filters
 */
export const clearLicenses = () => {
  searchPageStore.setState({
    licenses: DEFAULT_STATE.licenses,
    aiUsageAllowed: DEFAULT_STATE.aiUsageAllowed,
  })
}

// Deliberately not part of clearLicenses: the License dropdown and the More Filters resets call
// it, and a license-only reset must keep the route-default exclude_adult_content in place.
export const clearContentSafetyLicenses = () => {
  searchPageStore.setState({ contentSafetyLicenses: DEFAULT_STATE.contentSafetyLicenses })
}
